import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, inject, OnDestroy, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AlertService } from '@full-fledged/alerts';
import { UserService } from 'app/core/user/user.service';
import { ModalAuthService } from 'app/modules/auth/modal-auth/modal-auth.service';
import { FormNavigationService } from 'app/modules/forms/components/navigation';
import { FormService } from 'app/modules/forms/form.service';
import { toJson, trimTrailingSlash } from 'app/shared';
import { AccessLevel, DataContainer } from 'app/shared/model';
import { GlobalService } from 'app/shared/services/global.service';
import { SaveApplicationResponse } from 'app/shared/services/model/application-service.model';
import { catchError, Observable, of, Subject, switchMap, take, takeUntil } from 'rxjs';

export type SaveActionConfig = {
    organisationId: string;
    productId: string;
    data: any;
}

@Component({
    selector: 'save-action-button',
    template: '',
    // templateUrl: './save-and-print-button.component.html',
    // styleUrl: './save-and-print-button.component.scss',
    imports: [MatButtonModule, MatIconModule, MatProgressSpinnerModule],
})
export class SaveActionButtonBase implements OnDestroy, AfterViewInit {
    private _http: HttpClient = inject(HttpClient);
    private readonly _globalService = inject(GlobalService);
    private readonly _navigationService = inject(FormNavigationService);
    private readonly _userService = inject(UserService);
    private readonly _modalAuthService = inject(ModalAuthService);

    protected readonly _alertService = inject(AlertService);
    protected readonly _formService = inject(FormService);

    private baseHref = inject(APP_BASE_HREF);
    protected document = inject(DOCUMENT);

    public enabled = signal(false);
    public buttonVisible = signal(false);
    public saving = signal(false);

    _unsubscribeAll: Subject<boolean> = new Subject<boolean>();
    _hasUser: boolean = false;
    formAccessLevel = signal(AccessLevel.NoAccess);
    config = signal<SaveActionConfig[]>([]);
    currentConfig: SaveActionConfig | undefined = undefined;

    // buttonText = signal('');

    constructor() {
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntilDestroyed())
            .subscribe((user) => {
                this._hasUser = !!user;
            });

        // if the config list changes.. find matching config and check if the button should be displayed or not.
        toObservable(this.config)
            .pipe(takeUntilDestroyed())
            .subscribe({ next: () => this.checkVisible() })
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    ngAfterViewInit(): void {
        this.checkVisible();
    }

    loadConfig(filename: string) {
        let baseUrl = trimTrailingSlash(this.baseHref);
        this._http.get(`${ baseUrl }/config/${ filename }`)
            .pipe(takeUntil(this._unsubscribeAll)) // cancel this request if the user navigates away from the page
            .subscribe({
                next: (response: any) => {
                    // console.log(response);
                    this.config.set(response);
                }
            });
    }

    checkVisible() {
        // HIDE IF: preview || submits || pageType === 'receipt' || applicationEntitlement.BaseAccess !== 'W' || !isOnline || !prevPage
        let state = this._formService.currentState;
        this.currentConfig = this.config()?.find(item => !!item.productId && item.productId == state.formId)
            ?? this.config().find(item => !!item.organisationId && item.organisationId == state.organisationId);
        let enabled = this.currentConfig != undefined;
        this.enabled.set(enabled);

        let visible = this._globalService.isOnline()
            && this._navigationService.applicationEntitlement.baseAccess == AccessLevel.Write
            && state.pageId !== 999
            && !state.readonly;

        this.buttonVisible.set(visible);
    }

    protected saveApplication(): Observable<boolean> {
        if (!this._hasUser) {
            this.handleAuthModal();
            this.saving.set(false);
            return;
        }

        this.saving.set(true);
        return this.doSaveApplication()
            .pipe(
                takeUntil(this._unsubscribeAll),
                switchMap((result: SaveApplicationResponse) => {
                    if (result.success) {
                        this._alertService.success('Application saved successfully.');
                    } else {
                        this._alertService.danger('Application could not be saved.');
                    }

                    this.saving.set(false)
                    return of(true);
                }),
                catchError((err: any, caught: Observable<boolean>) => {
                    this._alertService.danger(err?.message || 'Application could not be saved');
                    this.saving.set(false);
                    return of(false);
                })
            );
    }

    handleAuthModal() {
        this._modalAuthService.showAuthRequiredModal(false, this.saveApplication.bind(this))
    }

    doSaveApplication(didCreateUser?: boolean, publicDidAuthenticate?: boolean): Observable<SaveApplicationResponse> {
        const state = this._formService.currentState;

        // save applicationdata (ie. container data) into application --> data object
        return this._formService.applicationData$
            .pipe(
                take(1),
                switchMap((applicationData: DataContainer) => {
                    return this._formService.saveApplication({
                        formId: state.formId,
                        formVersion: state.formVersion,
                        applicationId: state.applicationId,
                        offlineApplicationId: state.offlineApplicationId,
                        applicationState: state.workflowState,
                        data: toJson(applicationData),
                        publicDidAuthenticate: publicDidAuthenticate ?? false,
                        publicDidCreateAccount: didCreateUser ?? false,
                        isAutoSave: false, // isAutoSave ?? false,
                        pageId: state.pageId,
                        subpageId: state.subPageId || null,
                        created: state.created,
                        applicationIdDisplay: state.applicationIdDisplay,
                        userId: this._globalService.getCurrentUserId()
                    });
                }),
            );
    }
}
